import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { FeatureFlagServiceProps, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, Option } from '@mews-commander/types';
import { ServiceTimeClass } from '@mews/enums';
import { Stack } from '@optimus-web/core';
import { getAccountingCategoriesOptions } from '@mews-commander/legacy-entities/src/selectors/accountingCategories';
import { FieldsContainer } from '@mews-commander/ui';
import { InputField, SelectField, TimeStringPickerField } from '@mews-commander/legacy-form';
import { fieldNames } from '../constants';
import { BookableServiceCreateFormProps } from '../types';
import { getBookablePeriodOptions } from './selectors';

interface StateProps {
    accountingCategoriesOptions: Array<Option>;
    bookablePeriodOptions: Array<Option>;
}

interface OwnProps {
    onBookablePeriodChange: (value: ServiceTimeClass) => void;
    accountingCategorySetupRequired: boolean;
    timePickersVisible: boolean;
}

const BookableServiceCreateFormFields = ({
    accountingCategoriesOptions,
    accountingCategorySetupRequired,
    bookablePeriodOptions,
    timePickersVisible,
    onBookablePeriodChange,
}: StateProps & OwnProps) => (
    <FieldsContainer>
        <InputField {...bookableServiceNameProps} />
        <SelectField
            {...bookablePeriodProps}
            options={bookablePeriodOptions}
            onChange={(event: $TSFixMe, bookablePeriod: $TSFixMe) => {
                onBookablePeriodChange(bookablePeriod);
            }}
        />
        {timePickersVisible ? (
            <Stack distribution="stretch" spacing="200">
                <TimeStringPickerField {...arrivalTimeProps} />
                <TimeStringPickerField {...departureTimeProps} />
            </Stack>
        ) : null}
        {accountingCategorySetupRequired ? (
            <>
                <SelectField {...accountingCategoryProps} options={accountingCategoriesOptions} />
                <SelectField {...rebateAccountingCategoryProps} options={accountingCategoriesOptions} />
            </>
        ) : null}
    </FieldsContainer>
);

const bookableServiceNameProps = {
    label: <LocalizedText textKey={TextKey.Name} />,
    name: fieldNames.NAME,
    placeholder: <LocalizedText textKey={TextKey.ServiceNameHint} />,
    required: true,
};

const bookablePeriodProps = {
    label: <LocalizedText textKey={TextKey.BookablePeriod} />,
    name: fieldNames.BOOKABLE_PERIOD,
    placeholder: <LocalizedText textKey={TextKey.TimeUnitHint} />,
    bottomCaption: <LocalizedText textKey={TextKey.BookablePeriodAlertMessage} />,
    required: true,
};

const arrivalTimeProps = {
    label: <LocalizedText textKey={TextKey.ArrivalTime} />,
    name: fieldNames.ARRIVAL_TIME,
    required: true,
};

const departureTimeProps = {
    label: <LocalizedText textKey={TextKey.DepartureTime} />,
    name: fieldNames.DEPARTURE_TIME,
    required: true,
};

const accountingCategoryProps = {
    label: <LocalizedText textKey={TextKey.AccountingCategory} />,
    name: fieldNames.ACCOUNTING_CATEGORY_ID,
    required: true,
};

const rebateAccountingCategoryProps = {
    label: <LocalizedText textKey={TextKey.RebateAccountingCategory} />,
    name: fieldNames.REBATE_ACCOUNTING_CATEGORY_ID,
    required: true,
};

const inject = connect<StateProps, {}, BookableServiceCreateFormProps & FeatureFlagServiceProps, ApplicationState>(
    createStructuredSelector({
        accountingCategoriesOptions: getAccountingCategoriesOptions,
        bookablePeriodOptions: getBookablePeriodOptions,
    })
);

export default pipe(BookableServiceCreateFormFields, inject);
